import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GlobalContext } from '../GlobalContext'
import Button from '../formfields/Button'
import YellowAlert from '../ui-elements/YellowAlert'
import Card from '../ui-elements/Card'
import CollapsableCheckPeriodSummary from './CollapsableCheckPeriodSummary'
import { sortAllQuestionAnswersForCheckPeriod } from '../utils/sortAllQuestionAnswersForCheckPeriod'



type Props = {
}

function ProcessSummaryCheckPeriods({
}: Props) {

    const { processId } = useParams()
    const navigate = useNavigate()
    const {
        tableData,
        sendMessageToWebsocket,
        setShowModal,
        processData,
        checkIfThereAreQuestionsAndAnswersForChecklistPeriodId,
        t
    } = useContext(GlobalContext)
    const [waitingForChecklistId, setWaitingForChecklistId] = useState<string | null>(null)
    const [existingPeriodIds, setExistingPeriodIds] = useState<string[]>([])
    const [waitingForFirstQuestionPeriodId, setWaitingForFirstQuestionPeriodId] = useState<string | null>(null)


    const thisProcessData = processId && processData && processData[processId]
    const inProgress = thisProcessData && thisProcessData.InProgress
    const currentStageId = thisProcessData && thisProcessData.processRecord && thisProcessData.processRecord.CurrentStageId
    const thisStageDetails = thisProcessData && thisProcessData.Stages && currentStageId && thisProcessData.Stages[currentStageId]
    const isAdhoc = thisStageDetails && thisStageDetails.Behaviour === 'ongoing' && thisStageDetails.timedOrAdhoc === 'adhoc'
    const stageChecklists: string[] = (thisStageDetails && thisStageDetails.Checklists) || []

    const pendingCheckPeriods = thisProcessData && thisProcessData.PendingCheckPeriods && thisProcessData.PendingCheckPeriods.sort((a: any, b: any) => {
        return b.StartTimeStamp - a.StartTimeStamp
    })
    const completedCheckPeriods = thisProcessData && thisProcessData.CompletedCheckPeriods && thisProcessData.CompletedCheckPeriods.sort((a: any, b: any) => {
        return b.StartTimeStamp - a.StartTimeStamp
    })

    const hasPendingCheckPeriods = pendingCheckPeriods && pendingCheckPeriods.length > 0

    // Phase 1: detect the new check period appearing, then trigger a fetch of its questions/answers
    useEffect(() => {
        if (waitingForChecklistId && pendingCheckPeriods) {
            const newPeriod = pendingCheckPeriods.find(
                (p: any) => p.ChecklistId === waitingForChecklistId && !existingPeriodIds.includes(p.Id)
            )
            if (newPeriod) {
                setWaitingForChecklistId(null)
                setWaitingForFirstQuestionPeriodId(newPeriod.Id)
                checkIfThereAreQuestionsAndAnswersForChecklistPeriodId(newPeriod.Id)
            }
        }
    }, [pendingCheckPeriods, waitingForChecklistId])

    // Phase 2: once answers are loaded for the new period, navigate to the first question
    useEffect(() => {
        if (waitingForFirstQuestionPeriodId && tableData) {
            const sorted = sortAllQuestionAnswersForCheckPeriod(waitingForFirstQuestionPeriodId, tableData)
            if (sorted && sorted.length > 0) {
                const firstAnswer = sorted[0]
                setShowModal(null)
                setWaitingForFirstQuestionPeriodId(null)
                navigate(`/checklists/${waitingForFirstQuestionPeriodId}/${firstAnswer.Id}`)
            }
        }
    }, [tableData, waitingForFirstQuestionPeriodId])

    const startAdhocChecklist = (checklistId: string) => {
        const currentIds = pendingCheckPeriods ? pendingCheckPeriods.map((p: any) => p.Id) : []
        setExistingPeriodIds(currentIds)
        setWaitingForChecklistId(checklistId)
        setShowModal({ spinner: t('General.loading') })
        sendMessageToWebsocket(JSON.stringify({
            action: 'processesClient',
            subAction: 'startAdhocChecklist',
            checklistId,
            processRecordId: thisProcessData.processRecord.Id,
            stageId: currentStageId,
            processDefinitionId: thisProcessData.processRecord.ProcessDefinitionId,
        }))
    }

 

    const totalCheckPeriods = (pendingCheckPeriods && pendingCheckPeriods.length) + (completedCheckPeriods && completedCheckPeriods.length)

    if (totalCheckPeriods === 0 && !isAdhoc) {
        return null
    }

    return <div className={`w-full flex-1 h-full flex flex-col items-center gap-4`}>

        {isAdhoc && stageChecklists.length > 0 && !hasPendingCheckPeriods && <div className={`w-full flex flex-col gap-2`}>
            <p className={`font-bold`}>{t('ProcessPages.ProcessSummaryCheckPeriods.startACheck')}</p>
            {stageChecklists.map((checklistId: string) => {
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                const checklistName = thisChecklist && thisChecklist['ChecklistName']
                return <Button
                    key={checklistId}
                    onClick={() => startAdhocChecklist(checklistId)}
                    text={checklistName || checklistId}
                    fullwidth={true}
                    size={`big`}
                    variant={`primary`}
                />
            })}
        </div>}

        {pendingCheckPeriods && pendingCheckPeriods.length > 0 && <>
        <h4 className={`text-xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummaryCheckPeriods.checklistsToComplete')}</h4>
        <div className={`w-full flex flex-col gap-2`}>
            {pendingCheckPeriods && pendingCheckPeriods.map((thisCheckPeriod: any, index: number) => {
                const checklistId = thisCheckPeriod && thisCheckPeriod.ChecklistId
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                return <CollapsableCheckPeriodSummary
                    key={index}
                    checklistId={checklistId}
                    thisChecklist={thisChecklist}
                    checkPeriodId={thisCheckPeriod.Id}
                    thisCheckPeriod={thisCheckPeriod}
                    expandedByDefault={true}
                />
            })}
        </div>
        </>}
        {completedCheckPeriods && completedCheckPeriods.length > 0 && <div className={`w-full flex flex-col gap-2 items-center`}>
            <h4 className={`text-xl font-righteous text-brandblue`}>{t('ProcessPages.ProcessSummaryCheckPeriods.completedChecklists')}</h4>

        <div className={`w-full flex flex-col gap-2`}>
            {completedCheckPeriods.map((thisCheckPeriod: any, index: number) => {
                 const checklistId = thisCheckPeriod && thisCheckPeriod.ChecklistId
                const thisChecklist = tableData && tableData.Checklists && tableData.Checklists[checklistId]
                if (!thisChecklist){ 
                    console.error(`No checklist found for ID: ${checklistId} in completed check periods`)
                    return null 
                }
                return <CollapsableCheckPeriodSummary
                    key={index}
                    checklistId={checklistId}
                    thisChecklist={thisChecklist}
                    checkPeriodId={thisCheckPeriod.Id}
                    thisCheckPeriod={thisCheckPeriod}
                />
            })}   
        </div>
        </div>}
    </div>



}
export default ProcessSummaryCheckPeriods